import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import cn from 'classnames';
import { ONBOARDING_SKIP_HOME_EVENT, ONBOARDING_START_WIZARD_V2_EVENT, ONBOARDING_ADD_FOOTER, ONBOARDING_ADD_IMG } from '../../../constants/analyticEvents';
import { pushEvent } from '../../../utilities/analytics';
import { hideOnboarding, setWizardDesktop, setMobileHomepage, setCurrentStep } from '../onboardingSlice';
import { selectFilterData } from '../../../features/mainFilter/selectors';
import { getWizardV2State } from '../wizardService';
import showTrustBuilder from '../../../utilities/showTrustBuilder';
import { getParamFromUrl, isEntryFlow, removeParamFromUrl } from '../../../utilities/urlResolver';
import NumberAnimator from '../../../components/ui/NumberAnimator';
import abTesting from '../../../services/abTesting';
import useGetWelcomeMessage from '../../../hooks/wizard/useGetWelcomeMessage';
import useBodyScrollLock from '../../../hooks/useBodyScrollLock';
import { setLastChangedFilter } from '../../../appSlice';
import { setSecondaryFilterDefault } from '../../secondaryFilter/secondaryFilterSlice';
import { setMainFilterDefault } from '../../mainFilter/mainFilterSlice';
import { t } from '../../../utilities/translator';
import AIAssistantV2 from '../ai/v2/AIAssistantV2';

const WelcomeStep = () => {
  const dispatch = useDispatch();
  const currentStep = useSelector((state) => state.onboarding.currentStep);
  const { searchEngine } = useSelector(selectFilterData);
  const { loadingImg, counterText, showCounter, ...welcomeStep } = getWizardV2State().welcomeStep;
  const efMessageId = getParamFromUrl('pr_messageId');
  const showSkipButton = !abTesting.welcomeScreen.hideSkip && localStorage.getItem('onboardingCompleted');
  const hfLastFilters = JSON.parse(localStorage.getItem('hfLastFilters'));
  const startButton = hfLastFilters ? t('onboarding.startWizardReturning') : welcomeStep.startButton;

  useBodyScrollLock({ condition: true, mobileOnly: true });

  const {
    welcomeTitle,
    entryFlowMessageText,
    subtitleText,
    backgroundUrl,
    isDestinationId,
    isDestination,
  } = useGetWelcomeMessage();

  const [showCustomMessage, setShowCustomMessage] = useState(!(efMessageId && isDestinationId));

  useEffect(() => {
    // for the entry-flow links: show custom message after destination name loading
    if (efMessageId && isDestinationId && isDestination) {
      setShowCustomMessage(true);
    }
  }, [isDestinationId, isDestination]);

  useEffect(() => {
    const element = document.querySelector('.wizard-loader');
    if (element) {
      element.remove();
    }
    const wizardFooterLoader = document.querySelector('.wizard-footer--preload');
    if (wizardFooterLoader) {
      wizardFooterLoader.remove();
      pushEvent(ONBOARDING_ADD_FOOTER, { backgroundUrl, welcomeTitle, subtitleText, step: currentStep });
    }
    if (document.querySelector('#welcome-screen-bg-portal')) {
      pushEvent(ONBOARDING_ADD_IMG, { backgroundUrl });
    }
  }, [backgroundUrl, currentStep, subtitleText, welcomeTitle]);

  useEffect(() => {
    if (searchEngine && searchEngine.where && searchEngine.where.destinations) {
      const welcomeTitle = document.querySelector('.wizard-welcome-step__title2');
      if (welcomeTitle) {
        welcomeTitle.style.display = 'block';
      }
      const welcomeTitle2 = document.querySelector('.wizard-welcome-step__title2 b');
      if (welcomeTitle2) {
        welcomeTitle2.style.display = 'block';
      }
    }
  }, [searchEngine]);

  const hideStaticWizard = () => {
    document.getElementById('mobile-preload-container')?.remove();
  };

  window.hideClick = () => {
    if (isEntryFlow('link-presets') || isEntryFlow('link-presets-welcome')) {
      window.location.hash = '';
    }
    dispatch(hideOnboarding());
    showTrustBuilder(dispatch);
    if (window.pr_welcome) {
      pushEvent(ONBOARDING_SKIP_HOME_EVENT, { welcomePresets: window.pr_welcomeScreenToLoad, skipFrom: 'welcome' });
    } else {
      pushEvent(ONBOARDING_SKIP_HOME_EVENT, { skipFrom: 'welcome' });
    }
    hideStaticWizard();
    const skeleton = document.querySelector('.skeleton-without-offers');
    skeleton?.classList?.add('hide-skeleton');

    const skeletonWithOffers = document.querySelector('.skeleton-with-offers');
    skeletonWithOffers?.classList?.add('hide-skeleton');
    dispatch(setWizardDesktop(false));
    dispatch(setMobileHomepage(false));
  };

  const onStartClick = () => {
    dispatch(setCurrentStep(1));
    if (abTesting.mobileHomepageScrollable) {
      document.querySelector('body').classList.remove('mobile-wizzard-active-mobhp');
      document.querySelector('body').classList.add('mobile-wizzard-active');
    }
    if (window.pr_welcome) {
      pushEvent(ONBOARDING_START_WIZARD_V2_EVENT, { welcomePresets: window.pr_welcomeScreenToLoad });
    } else {
      pushEvent(ONBOARDING_START_WIZARD_V2_EVENT);
    }
  };

  const onSkipClick = () => {
    window.hideClick();
    if (isEntryFlow('link-presets') || isEntryFlow('link-presets-welcome')) {
      removeParamFromUrl(['pr_entryFlow']);
    }
    if (window.location.search.includes('startWizard=true')) {
      const url = new URL(window.location);
      url.searchParams.delete('startWizard');
      window.history.replaceState({}, document.title, url.toString());
    }
    document.querySelector('body').classList.remove('mobile-wizzard-active-mobhp');
    pushEvent(ONBOARDING_SKIP_HOME_EVENT, { skipFrom: 'welcome' });
    dispatch(setLastChangedFilter('what'));
    dispatch(setSecondaryFilterDefault());
    dispatch(setMainFilterDefault());
    dispatch(setWizardDesktop(false));
    dispatch(setMobileHomepage(false));
    document.dispatchEvent(new CustomEvent('wizard closed'));
  }

  const wizardPortal = document.querySelector('#mobile-preload-container .wizard-layout.welcome-screen');
  const welcomeScreenBackgroundPortal = document.querySelector('#welcome-screen-bg-portal');

  const welcomeScreenBackground = (
    <div className={cn('welcome-screen-background', { 'welcome-screen-background--animated': !loadingImg })} style={{ backgroundImage: `url(${backgroundUrl})` }} />
  );

  const wizardFooter = (
    <div className="wizard-footer">
      <div className="flex flex-col gap-10 text-center">
        {/* Temporarly hidden from mobile
        <LastSearch /> */}

        <div className="text-white">
          {showCounter && !isEntryFlow('link-presets-welcome') && (
            <div className="animated-counter mb-1.5">
              <NumberAnimator number={window.holidayFinder?.welcomeScreenCounter[window.holidayFinder.market]} />
            </div>
          )}
          {!isEntryFlow('link-presets-welcome') && <div className="text-lg leading-6">{counterText}</div>}
          {subtitleText && <div className={cn("mt-8 text-lg font-semibold leading-5 welcome-custom-message", { "welcome-show-message": showCustomMessage })}>{subtitleText}</div>}
        </div>


        <div className="buttons-list animated fadeIn">
          <button className="welcome-screen-btn text-xl font-semibold leading-none flex items-center justify-center gap-2 rounded-full" onClick={onStartClick}>
            <span>{startButton}</span>
            <i className="ri-arrow-right-s-line text-2xl leading-none rtl:-scale-x-100" />
          </button>
          <button className={cn('welcome-screen-btn welcome-screen-btn__skip heading-tiny leading-none', { 'is-hidden': !showSkipButton })} onClick={onSkipClick}>
            {t('onboarding.skipWizard')}
          </button>
          {abTesting.mobileHomepageScrollable && (
            <div className="mouse-scroll-ico" />
          )}
        </div>
      </div>
    </div>
  );

  let footer = wizardFooter

  if (abTesting.aiAssistantV2) {
    footer = (
      <>
        <div className="assistant-v2-gradient" />
        <AIAssistantV2 onSkip={onSkipClick} />
      </>
    )
  }

  return (
    <>
      <div className="wizard-welcome-step">
        <div className="wizard-welcome-step__title2">
          {!!welcomeTitle && <span>{welcomeTitle}</span>}
          {efMessageId && (
            <b>
              {entryFlowMessageText}
            </b>
          )}
        </div>
      </div>
      {createPortal(welcomeScreenBackground, welcomeScreenBackgroundPortal)}
      {wizardPortal
        ? createPortal(footer, wizardPortal)
        : footer
      }
    </>
  );
};

export default WelcomeStep;
